import * as Sentry from "@sentry/react"

import supabase from "@/supabase"
import { getFunctionsErrorMessage } from "@/utils/supabaseFunctionsError"

// Type definitions for driver onroad test assessment
export interface DriverOnroadTestAssessment {
  id?: string
  application_id: string
  type: "driver-onroad-test"
  started_at?: string
  completed_at?: string
  question_num?: number
  result_breakdown?: Record<string, unknown>
  result?: "pass" | "fail"
  score?: number
}

// Type for assessment update data
export interface AssessmentUpdateData {
  id: string
  question_num?: number
}

/**
 * Create a new driver onroad test assessment via cloud function
 */
export const createDriverOnroadTestAssessment = async (
  assessmentData: Partial<DriverOnroadTestAssessment>,
): Promise<{
  success: boolean
  message: string
  assessment?: DriverOnroadTestAssessment
}> => {
  try {
    const { data, error } = await supabase.functions.invoke("create-assessment", {
      body: assessmentData,
    })

    if (error) {
      throw new Error(
        (await getFunctionsErrorMessage(error)) || "Failed to create driver onroad test assessment",
      )
    }

    return {
      success: true,
      message: "Driver onroad test assessment created successfully",
      assessment: data.assessment,
    }
  } catch (error) {
    console.error("Error in createDriverOnroadTestAssessment:", error)

    Sentry.captureException(error, {
      extra: {
        context: "driverOnroadTestService.createDriverOnroadTestAssessment",
        assessmentData: JSON.stringify(assessmentData),
      },
    })

    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Failed to create driver onroad test assessment",
    }
  }
}

/**
 * Update an existing driver onroad test assessment via cloud function
 */
export const updateDriverOnroadTestAssessment = async (
  updateData: Partial<AssessmentUpdateData>,
): Promise<{
  success: boolean
  message: string
  assessment?: DriverOnroadTestAssessment
}> => {
  try {
    const { data, error } = await supabase.functions.invoke("update-assessment", {
      body: updateData,
    })

    if (error) {
      throw new Error(
        (await getFunctionsErrorMessage(error)) || "Failed to update driver onroad test assessment",
      )
    }

    return {
      success: true,
      message: "Driver onroad test assessment updated successfully",
      assessment: data.assessment,
    }
  } catch (error) {
    console.error("Error in updateDriverOnroadTestAssessment:", error)

    Sentry.captureException(error, {
      extra: {
        context: "driverOnroadTestService.updateDriverOnroadTestAssessment",
        assessmentId: updateData.id,
      },
    })

    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Failed to update driver onroad test assessment",
    }
  }
}
